import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortByKey } from '@semios/app-platform-common'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { DropdownSelectorProperty } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorProperty/DropdownSelectorProperty'
import type { GridTableContentSection, GridTableContentSectionItem } from 'components/GridTable/types'
import { translate } from 'i18n/i18n'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesBlockHaveValueType } from 'utils/doesFieldAssetHaveValueType'
import { filterFieldAssetsByValueTypes } from 'utils/filterFieldAssetsByValueTypes'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { PowderyMildewInfectionRiskIndex } from '../../../../../_utils/by-domain/powderyMildew/PowderyMildewInfectionRiskIndex'
import { PowderyMildewTooltip } from '../../../../../_utils/by-domain/powderyMildew/PowderyMildewTooltip'
import { BlockWithWeatherStationLabel } from '../../../../BlockWithWeatherStationLabel/BlockWithWeatherStationLabel'
import { blockTimestampToValuesDictionaryMaker, defaultValuesRowHeight } from '../../_utils'

type TValueTypeKey = VV.DomainTypes.CherryPowderyMildew.TTimeseriesValueTypeKeysMerged

const valueTypeKey: TValueTypeKey = 'cherryPowderyMildewModelPredictedRisk'
const permission = 'VIEW_CHERRY_POWDERY_MILDEW_DETAILS'
const checkPermission = () => selectedPropertyHasPermission({ permission })
const preferredAggregationInterval = { preferredAggregationInterval: 'DAILY' } as const

const getValuesRequestedForBlocks = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!checkPermission()) return {}

  const blocks = fieldAssetStore.getState()?.properties?.[Number(selectedFieldAssets.property)]?.blocks

  if (!blocks) return {}

  const valuesRequested: Partial<
    Record<
      VV.DomainTypes.CherryPowderyMildew.TTimeseriesValueTypeKeysMerged,
      typeof preferredAggregationInterval
    >
  > = {}

  Object.entries(selectedValueGroups).forEach(([valueGroup, isActive]) => {
    if (!!isActive && valueGroup.includes('cherry_powdery_mildew')) {
      if (!selectedPropertyHasPermission({ permission })) return

      valuesRequested[valueTypeKey] = preferredAggregationInterval
    }
  })

  if (Object.keys(valuesRequested).length === 0) return {}

  const relevantBlocksForProperty = filterFieldAssetsByValueTypes({
    fieldAssets: Object.values(blocks),
    valuesTimeseries: [valueTypeKey],
  })

  const valuesToBeRequested: Partial<routes.Values.Request> = {}

  valuesToBeRequested.blocks = {
    blockIds: relevantBlocksForProperty.map((block) => block.blockId),
    valuesRequested,
  }

  return valuesToBeRequested
}

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  const blockValues = getValuesRequestedForBlocks({ selectedValueGroups, selectedFieldAssets })

  return { ...blockValues }
}

export const content = ({ data }: { data: routes.Values.Response }): GridTableContentSection => {
  const selectedFieldAssets = selectedFieldAssetsStore.getState()
  const selectedProperty = selectedFieldAssets.property
  const fieldAssets = fieldAssetStore.getState().properties
  const property = selectedProperty ? fieldAssets?.[selectedProperty] : null
  const points = property?.points ?? {}

  const commonReturnItems = {
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{unitConverter.cherryPowderyMildew().titleWithoutUnit()}</span>
        <PowderyMildewTooltip crop={'cherry'} />
      </div>
    ),
    fieldAssetPicker: (
      <DropdownSelectorProperty valuesTimeseriesToFilterOn={[]} propertyPermissionsToCheck={[permission]} />
    ),
    id: `summary-grid-cherry-powdery-mildew-risk-${selectedProperty}`,
  }

  if (!checkPermission() && selectedProperty) {
    return { ...propertyLacksPermissionSectionMaker(commonReturnItems) }
  }

  const items: (GridTableContentSectionItem & { blockName: string })[] = []

  Object.keys(data?.blocks || []).forEach((blockId) => {
    const block = property?.blocks?.[Number(blockId)]

    if (
      block &&
      doesBlockHaveValueType({
        propertyId: Number(selectedProperty),
        blockId: Number(blockId),
        timeseriesValueToCheck: valueTypeKey,
      })
    ) {
      const weatherStationLabel =
        !!block.associatedWeatherStation && points[block.associatedWeatherStation]?.name
          ? points[block.associatedWeatherStation].name
          : translate.phrases.banyanApp('Unknown Weather Station')

      const weatherStationExternalVendorNames = block.associatedWeatherStation
        ? points[block.associatedWeatherStation]?.externalVendorNames
        : undefined

      const label = block.name || translate.phrases.banyanApp('Unnamed Block')

      const blockItem: GridTableContentSectionItem & { blockName: string } = {
        blockName: label,
        id: `cpm-secondary-infection-monitoring-${blockId}`,
        label: (
          <BlockWithWeatherStationLabel
            label={label}
            weatherStationLabel={weatherStationLabel}
            weatherStationExternalVendorNames={weatherStationExternalVendorNames}
          />
        ),
        height: defaultValuesRowHeight,
        labelMinWidth: 220,
        valueMinWidth: 130,
        render: (dataPoint: { timestamp: string; value: number | null } | undefined) => {
          if (dataPoint === undefined || dataPoint === null || dataPoint.value === null)
            return translate.phrases.templates('-')

          return <PowderyMildewInfectionRiskIndex value={dataPoint.value} />
        },
        values: blockTimestampToValuesDictionaryMaker(data, valueTypeKey, blockId),
      }

      items.push(blockItem)
    }
  })

  return {
    ...commonReturnItems,
    items: items.filter(Boolean).sort(sortByKey('blockName')),
  }
}
